import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { getLaboklinApi } from '../../../services/laboklinApi';
import { LABOKLIN_BORDER, LABOKLIN_RED } from '../../../theme/laboklinTheme';
import {
  BatchDetail,
  OUTCOME_LABELS,
  SectionStability,
  BatchHeader,
  ReportScore,
  gradeOf,
} from '../../../types/labConsistency';

/**
 * The batch: one row per (report, step), worst first.
 *
 * Verdict is the first column and the primary sort, because a split verdict outranks
 * any section score. A report that shows diagnoses on some runs and refuses on others
 * is a worse problem than one whose differential merely wobbles - both answers reached
 * a veterinarian as fact, and one of them was built on values the laboratory itself
 * called uninterpretable.
 */

const GRADE_COLOUR = {
  good: 'rgb(30, 130, 60)',
  warn: 'rgb(190, 130, 20)',
  bad: LABOKLIN_RED,
} as const;

/**
 * One section's two questions, said in words a veterinarian can check by eye.
 *
 * "0.80 1.00" is our vocabulary, not theirs, and it hides which of two very different
 * failures happened: a section that vanished on some runs, or one that appeared every
 * time saying something different. The counts name it.
 */
const Score: React.FC<{ section: SectionStability; runs: number; what: string }> = ({
  section,
  runs,
  what,
}) => {
  const shownIn = new Set(section.items.flatMap((i) => i.presentIn)).size;
  const settled = section.items.filter((i) => i.runs === section.runs).length;
  const explain = (
    <Box sx={{ p: 0.5 }}>
      <Typography sx={{ fontSize: 12, mb: 0.75 }}>
        <strong>Was it there?</strong>{' '}
        {shownIn === runs
          ? `The ${what} section appeared in all ${runs} runs.`
          : `The ${what} section appeared in ${shownIn} of ${runs} runs and was missing from the rest.`}
      </Typography>
      <Typography sx={{ fontSize: 12 }}>
        <strong>Was it the same?</strong>{' '}
        {section.items.length === 0
          ? 'Nothing was offered in any run, so there is nothing to compare.'
          : `${settled} of the ${section.items.length} ${what} named across all runs appeared every single time. The rest came and went.`}
      </Typography>
    </Box>
  );
  return (
    <Tooltip title={explain} arrow placement="top">
    <Box sx={{ cursor: 'help' }}>
      <Typography sx={{ fontSize: 12, color: GRADE_COLOUR[gradeOf(section.presenceAgreement, 'presence')] }}>
        {shownIn === runs ? 'shown every time' : shownIn === 0 ? 'never shown' : `shown ${shownIn}/${runs}`}
      </Typography>
      <Typography sx={{ fontSize: 12, color: GRADE_COLOUR[gradeOf(section.contentAgreement, 'content')] }}>
        {section.items.length === 0 ? '—' : `${settled}/${section.items.length} the same`}
      </Typography>
    </Box>
    </Tooltip>
  );
};

/** A column heading that can explain itself, since none of these are self-evident. */
const Head: React.FC<{ label: string; help: string }> = ({ label, help }) => (
  <Tooltip title={<Typography sx={{ fontSize: 12, p: 0.5 }}>{help}</Typography>} arrow placement="top">
    <Box component="span" sx={{ cursor: 'help', borderBottom: '1px dotted rgba(0,0,0,0.35)' }}>
      {label}
    </Box>
  </Tooltip>
);

const COLUMNS: Array<[string, string]> = [
  ['Report', 'The Laboklin report number.'],
  [
    'Times run',
    'The same report was interpreted this many times, from scratch, with no memory of the previous attempts. Everything to the right compares those answers with each other.',
  ],
  [
    'What you got',
    'Whether a differential was produced at all. "Split" means the runs disagreed about even that — some produced diagnoses and others did not, from the same report.',
  ],
  ['Diagnoses', 'The list of possible diagnoses shown to you.'],
  ['Set aside', 'Diagnoses the assistant ruled out, with its reason.'],
  ['Follow-up labs', "Additional tests offered from Laboklin's catalogue."],
  ['Your review', 'How many of the sections on this report you have assessed.'],
];

export const ConsistencyBatchView: React.FC = () => {
  const { batchId } = useParams<{ batchId?: string }>();
  const navigate = useNavigate();
  const [batches, setBatches] = useState<BatchHeader[] | null>(null);
  const [detail, setDetail] = useState<BatchDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getLaboklinApi()
      .listConsistencyBatches()
      .then(({ batches: list }) => {
        setBatches(list);
        // No batch in the path: open the newest, which is what anyone arriving here wants.
        if (!batchId && list.length > 0) {
          navigate(`/mylab2/consistency-reports/${list[0].batchId}`, { replace: true });
        }
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [batchId, navigate]);

  useEffect(() => {
    if (!batchId) return;
    let live = true;
    setDetail(null);
    getLaboklinApi()
      .getConsistencyBatch(batchId)
      .then((d) => live && setDetail(d))
      .catch((e) => live && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      live = false;
    };
  }, [batchId]);

  const assessedCount = useMemo(() => {
    const counts = new Map<string, number>();
    for (const a of detail?.assessments ?? []) {
      const key = `${a.reportNo}#${a.step}`;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return counts;
  }, [detail]);

  const rows = useMemo(() => {
    const reports = [...(detail?.reports ?? [])];
    // A split outcome outranks any section score: the same report answered two
    // different ways, both reaching a veterinarian as fact.
    return reports.sort(
      (a, b) =>
        a.outcome.agreement - b.outcome.agreement ||
        worst(a) - worst(b) ||
        a.reportNo.localeCompare(b.reportNo)
    );
  }, [detail]);

  if (error) return <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>;
  if (!batches) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={24} /></Box>;
  if (batches.length === 0) {
    return (
      <Alert severity="info" sx={{ m: 3 }}>
        No consistency batch has been published yet.
      </Alert>
    );
  }

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', p: { xs: 2, md: 3 } }}>
      <Typography sx={{ fontSize: 20, fontWeight: 600 }}>Consistency reports</Typography>
      <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 2 }}>
        Each report was run several times through the real assistant. These numbers say only
        whether the answer came out the same each time — whether it is a good answer is what
        your assessment adds.
      </Typography>

      <Select
        size="small"
        value={batchId ?? batches[0].batchId}
        onChange={(e) => navigate(`/mylab2/consistency-reports/${e.target.value}`)}
        sx={{ mb: 2, minWidth: 320, fontSize: 13 }}
      >
        {batches.map((b) => (
          <MenuItem key={b.batchId} value={b.batchId} sx={{ fontSize: 13 }}>
            {b.label} · {new Date(b.generatedAt).toLocaleDateString()} · {b.runsPerReport} runs each
          </MenuItem>
        ))}
      </Select>

      {!detail ? (
        <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={24} /></Box>
      ) : (
        <Paper elevation={0} sx={{ border: `1px solid ${LABOKLIN_BORDER}`, borderRadius: 1, overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {COLUMNS.map(([label, help]) => (
                  <TableCell key={label} sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>
                    <Head label={label} help={help} />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => {
                const o = r.outcome;
                const split = o.agreement < 1;
                const gotDifferential = !split && o.majority === 'differential';
                /**
                 * "No differential" is stated, never scored, and never coloured as a
                 * fault. A panel with nothing out of range should produce no diagnosis -
                 * one report here carries 47 analytes with none abnormal - so whether
                 * nothing was right is a clinical judgement and this table does not make
                 * it. The abnormal count is shown so it can be made.
                 */
                const shape =
                  o.abnormalCount !== null ? `${o.abnormalCount}/${o.analyteCount} abnormal` : null;
                return (
                  <TableRow
                    key={`${r.reportNo}#${r.step}`}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/mylab2/consistency-reports/${r.batchId}/${r.reportNo}`)}
                  >
                    {/*
                      No "step" column. It is the harness's two-step protocol - whether
                      the submitting vet's history was withheld - which is our vocabulary,
                      and every row in a normal batch carries the same value anyway.
                    */}
                    <TableCell sx={{ fontSize: 13, fontWeight: 500 }}>{r.reportNo}</TableCell>
                    <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>{o.runs}</TableCell>
                    <TableCell>
                      <Typography
                        sx={{
                          fontSize: 12,
                          fontWeight: split ? 700 : 400,
                          color: split ? LABOKLIN_RED : 'text.primary',
                        }}
                      >
                        {split
                          ? `split — ${o.differential} differential / ${o.noDifferential} none / ${o.notDiagnostic} not diagnostic`
                          : OUTCOME_LABELS[o.majority]}
                      </Typography>
                      {!split && o.majority !== 'differential' && shape && (
                        <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                          {shape} · every run
                        </Typography>
                      )}
                    </TableCell>
                    {gotDifferential ? (
                      <>
                        <TableCell><Score section={r.differentials} runs={o.runs} what="diagnoses" /></TableCell>
                        <TableCell><Score section={r.setAside} runs={o.runs} what="set-aside diagnoses" /></TableCell>
                      </>
                    ) : (
                      // Only these two depend on there being a differential. Printing
                      // 1.00 for them is how a report that produced no answer at all
                      // became the greenest row in the table.
                      <TableCell colSpan={2} sx={{ fontSize: 12, color: 'text.disabled', fontStyle: 'italic' }}>
                        nothing to compare
                      </TableCell>
                    )}
                    {/*
                      Follow-up labs are graded whatever the outcome. They do not need a
                      differential to be worth offering - one report here suggests a
                      Toxoplasma PCR in every run and no diagnosis at all - and hiding
                      them concealed a report whose five runs proposed five completely
                      disjoint sets of tests.
                    */}
                    <TableCell><Score section={r.tests} runs={o.runs} what="tests" /></TableCell>
                    <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>
                      {assessedCount.get(`${r.reportNo}#${r.step}`) ?? 0}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 1.5, maxWidth: 760 }}>
        Each section cell answers two questions: did it appear at all, and did it say the same
        thing when it did. Hover any heading or cell for the detail. &ldquo;No differential&rdquo;
        is not marked as a fault — on a panel with nothing out of range it is the right answer,
        and whether it was right here is exactly what your assessment adds.
      </Typography>
    </Box>
  );
};

/** The lowest score on a row, used to sort the worst reports to the top. */
function worst(r: ReportScore): number {
  return Math.min(
    r.differentials.presenceAgreement,
    r.differentials.contentAgreement,
    r.setAside.presenceAgreement,
    r.setAside.contentAgreement,
    r.tests.presenceAgreement,
    r.tests.contentAgreement
  );
}

export default ConsistencyBatchView;
